/**
 * Easy Suite SSO Callback Component
 * 
 * Ce composant gère la redirection depuis Easy Suite après authentification.
 * Il vérifie le token, crée/connecte l'utilisateur et redirige vers le dashboard.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { SSO_VERIFY_AND_LOGIN_API_ROUTE } from 'routes/api/endpoints';
import { setCredentials } from 'redux/features/userSliceWithTokenValidation';
import { rxdApi } from 'actions/rtkquery';
import { ACCEUIL_PATH } from 'routes/navigation/navigationPaths';
import './EasySuiteCallback.css';

const EasySuiteCallback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasRun = useRef(false);

  useEffect(() => {
    // Empêcher la double exécution (React 18 Strict Mode)
    if (hasRun.current) return;

    const handleCallback = async () => {
      try {
        // 1. Récupérer le token de l'URL
        const easySuiteToken = searchParams.get('token');
        
        if (!easySuiteToken) {
          throw new Error("Token manquant dans l'URL");
        }

        console.log('🔐 Token Easy Suite reçu');

        // 2. Marquer comme exécuté et nettoyer l'URL
        hasRun.current = true;
        searchParams.delete('token');
        setSearchParams(searchParams, { replace: true });

        // 3. Appeler l'API backend pour vérifier le token
        const response = await axios.post(
          SSO_VERIFY_AND_LOGIN_API_ROUTE,
          { token: easySuiteToken },
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
          }
        );

        const data = response.data;

        if (!data.success) {
          throw new Error(data.message || "Erreur d'authentification");
        }

        console.log('✅ Authentification réussie');

        // 4. Préparer les données pour Redux (format identique à la connexion normale)
        const credentials = {
          access: data.token,
          refresh: data.refresh,
          ...data.user
        };

        // 5. Vider le cache API puis mettre à jour Redux (comme ConnexionForm)
        dispatch(rxdApi.util.resetApiState());
        dispatch(setCredentials(credentials));

        // 6. Rediriger vers ACCEUIL_PATH (comme ConnexionForm)
        navigate(ACCEUIL_PATH, { replace: true });

      } catch (err) {
        console.error('❌ Erreur SSO:', err);
        setError(err.response?.data?.message || err.message || "Erreur d'authentification");
        
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/connexion', { replace: true });
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, setSearchParams, navigate, dispatch]);

  if (loading) {
    return (
      <div className="sso-callback-container">
        <div className="sso-callback-content">
          <div className="spinner">
            <div className="spinner-border text-[#008180]" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
          <h2 className="mt-4">Connexion en cours...</h2>
          <p className="text-muted">Vérification de votre identité avec Easy Suite</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sso-callback-container">
        <div className="sso-callback-content">
          <div className="error-icon">
            <i className="bi bi-x-circle text-danger" style={{ fontSize: '4rem' }}></i>
          </div>
          <h2 className="mt-4 text-danger">Erreur dauthentification</h2>
          <p className="text-muted">{error}</p>
          <p className="text-muted">Redirection vers la page de connexion...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default EasySuiteCallback;
